<script setup>
import { useResizeObserver } from '@vueuse/core'
import { ref, useTemplateRef, computed, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

import { SAML_DOMAIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { OKR_STATUSES } from '@/utils/objectives'
import { createDefaultDomainModel, SSO_NAME_MAX_LENGTH } from '@/utils/sso-connection-settings'

import AppFieldError from '@/components/form/AppFieldError'
import ObjectiveStatus from '@/components/objectives/ObjectiveStatus'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppInfoMessage from '@/components/ui/AppInfoMessage'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'

defineOptions({
  name: 'DomainsList'
})

const props = defineProps({
  readonly: {
    type: Boolean
  },

  loading: {
    type: Boolean
  }
})

const model = defineModel({
  type: Array,
  required: true
})

const { t } = useI18n()

const { ID, DOMAIN_NAME, IS_UNIQUE, IS_VALID, STATUS_ID } = SAML_DOMAIN_ENTITY_KEYS

const onAddDomain = () => {
  model.value.push({
    ...createDefaultDomainModel()
  })
}

const domainsListReference = useTemplateRef('domains-list')

const isListScrollable = ref(false)

useResizeObserver(domainsListReference, entries => {
  const { target } = entries[0]

  isListScrollable.value = target && target.clientHeight < target.scrollHeight
})

const onRemoveDomain = domainId => {
  model.value = model.value.filter(d => d.id !== domainId)
}

const domainsErrors = computed(() => {
  if (props.readonly) return {}

  return model.value.reduce((acc, domain) => {
    const { isValid, isUnique } = domain

    const [errorMessage] = [
      !isValid && t('sso_domains_errors[0]'),
      !isUnique && t('sso_domains_errors[1]')
    ].filter(Boolean)

    if (errorMessage) {
      return {
        ...acc,
        [domain.id]: errorMessage
      }
    }

    return acc
  }, {})
})

const removeErrors = domainId => {
  const domain = model.value.find(d => d.id === domainId)

  if (domain) {
    domain[IS_VALID] = true
    domain[IS_UNIQUE] = true
  }
}

const domainInputsReferences = ref({})

const moveToFirstInvalidDomain = () => {
  if (props.readonly) return
  nextTick(() => {
    const firstInvalidDomainId = Object.keys(domainsErrors.value)[0]

    if (domainInputsReferences.value[firstInvalidDomainId]) {
      nextTick(() => {
        domainInputsReferences.value[firstInvalidDomainId].scrollIntoView({
          behavior: 'smooth'
        })
      })
    }
  })
}

defineExpose({
  moveToFirstInvalidDomain
})

onBeforeUnmount(() => {
  domainInputsReferences.value = {}
})

const MAX_ROWS_TO_SHOW = 7
</script>

<template>
  <div class="dl-DomainsWrapper">
    <AppInfoMessage class="dl-Description">
      <slot name="message">
        {{ t('sso.add_domains.message') }}
      </slot>
    </AppInfoMessage>

    <div
      ref="domains-list"
      :class="{
        'dl-DomainsList-bordered': model.length > MAX_ROWS_TO_SHOW,
        'dl-DomainsList-scrollable': isListScrollable
      }"
      class="dl-DomainsList"
    >
      <div v-for="domain in model" :key="domain[ID]" class="dl-DomainsList_Item">
        <div v-if="readonly" class="dl-DomainItemInfo">
          <span class="oboard-truncated-text">
            {{ domain.domainName }}
          </span>
          <div class="dl-DomainInfoStatus">
            <ObjectiveStatus
              :status="domain[STATUS_ID] ? OKR_STATUSES.ON_TRACK : OKR_STATUSES.AT_RISK"
            >
              {{ domain[STATUS_ID] ? t('sso.domain.verified') : t('sso.domain.not_verified') }}
            </ObjectiveStatus>
          </div>
        </div>

        <template v-else>
          <div :ref="el => (domainInputsReferences[domain[ID]] = el)" class="dl-DomainItemInput">
            <AppInput
              v-model.lowercase="domain[DOMAIN_NAME]"
              :max-length="SSO_NAME_MAX_LENGTH"
              :is-error="Boolean(domainsErrors[domain[ID]])"
              :placeholder="t('sso.domain_name.placeholder')"
              :readonly="readonly || loading"
              size="xlg"
              style-type="primary"
              :auto-focus="!readonly"
              @focus="removeErrors(domain[ID])"
            />
          </div>

          <div v-if="model.length > 1 && !readonly" class="dl-DomainItemActions">
            <AppButton
              class="dl-ActionButton"
              icon="delete-next"
              size="sm"
              :disable="loading"
              type="ghost-next"
              @click="onRemoveDomain(domain[ID])"
            />
          </div>

          <AppFieldError
            v-if="Boolean(domainsErrors[domain[ID]])"
            class="dl-DomainItemError"
            :show="Boolean(domainsErrors[domain[ID]])"
          >
            {{ domainsErrors[domain[ID]] }}
          </AppFieldError>
        </template>
      </div>
    </div>

    <AppTableCreateButton
      v-if="!readonly"
      class="dl-AddDomainButton"
      icon-name="plus-small"
      no-hover
      no-padding
      :disable="loading"
      @click="onAddDomain"
    >
      {{ t('sso.add_domain') }}
    </AppTableCreateButton>
  </div>
</template>

<style scoped lang="scss">
@import '~@/assets/styles/mixins';

.dl-Description,
.dl-AddDomainButton {
  margin-block: 20px;
}

.dl-DomainsList {
  display: flex;
  flex-direction: column;
  @include styled-native-scrollbar();

  --mdm-list-gap: 12px;
  gap: var(--mdm-list-gap);

  padding-block: 6px;
  overflow-y: auto;

  --mdm-row-height: 40px;

  $max-rows-to-show: v-bind(MAX_ROWS_TO_SHOW);
  $calculated-max-height: calc(
    (var(--mdm-row-height) + var(--mdm-list-gap)) * #{$max-rows-to-show}
  );

  max-height: $calculated-max-height;

  &-scrollable {
    padding-right: 10px;
  }

  &-bordered {
    box-shadow: inset 0 -1px 0 0 $grey-2-next, inset 0 1px 0 0 $grey-2-next;
  }
}

.dl-DomainsList_Item {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dl-DomainItemInput {
  flex-grow: 1;
}

.dl-DomainItemActions {
  flex-shrink: 0;
  display: inherit;
  gap: inherit;
}

.dl-ActionButton {
  color: $dark-2;
}

.dl-DomainItemError {
  width: 100%;
  flex-shrink: 0;
}

.dl-DomainItemInfo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  background-color: $grey-3-next;
  border-radius: $border-radius-sm-next;
  height: var(--mdm-row-height);
  padding-inline: 10px 8px;
  flex-grow: 1;
}

.dl-DomainInfoStatus {
  flex-shrink: 0;
  --os-text-transform: none;
}
</style>
